import { useCallback, useMemo, useState } from 'react';

import { useToast } from '../../../shared/toast/toastContext';

import type { ActiveDialog } from './components/UsersDialogs';
import UsersErrorState from './components/UsersErrorState';
import UsersLoadingState from './components/UsersLoadingState';
import UsersPageBody from './components/UsersPageBody';
import { computeUsersKpis, filterUserRows } from './filterUserRows';
import { useUsersFilterParams } from './use-users-filter-params';
import { handleResendInvite } from './users-page-actions';
import type { UserRow } from './userViewModel';
import { useUsersPageData } from './useUsersPageData';

/** User Management — the primary surface under Settings · Users.
 *
 *  See the "User Management & Permissions" Linear project for the design brief:
 *  https://linear.app/bonsai-bar/project/user-management-and-permissions-789cf9a8dda1
 *  The list is the foundation; Deactivate / Edit-role / Invite / Revoke-invite /
 *  Audit dialogs layer on via UsersDialogs.
 */
export default function UsersPage() {
  const data = useUsersPageData();
  const filterableRegionCodes = useMemo(
    () => (data.scopedRegionName ? [] : data.regionOptions.map((r) => r.id)),
    [data.scopedRegionName, data.regionOptions],
  );
  const [filters, setFilters] = useUsersFilterParams(filterableRegionCodes);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<ActiveDialog>(null);
  const { showToast } = useToast();

  const handleMutationSuccess = useCallback(async () => {
    setDialog(null);
    await data.refetch();
  }, [data]);

  const onResendInvite = useCallback(
    (user: UserRow) => void handleResendInvite(user, showToast, data.refetch),
    [showToast, data.refetch],
  );

  const visibleRows = useMemo(
    () => filterUserRows(data.inScopeRows, filters),
    [data.inScopeRows, filters],
  );
  const { kpis, importedCount } = useMemo(
    () => computeUsersKpis(data.inScopeRows),
    [data.inScopeRows],
  );
  // Only show the detail pane when the user actively selects a row — the
  // previous "fall back to the first row" behavior left the pane always open,
  // which conflicts with the floating-over-the-list interaction (the list
  // would be perpetually grayed out).
  const selected = useMemo(
    () => (selectedId ? (visibleRows.find((r) => r.id === selectedId) ?? null) : null),
    [visibleRows, selectedId],
  );

  if (data.loading) return <UsersLoadingState />;
  if (data.error) return <UsersErrorState message={data.error} />;

  return (
    <UsersPageBody
      data={data}
      filters={filters}
      onFiltersChange={setFilters}
      visibleRows={visibleRows}
      kpis={kpis}
      importedCount={importedCount}
      selected={selected}
      onSelect={(row) => setSelectedId(row.id)}
      onClearSelection={() => setSelectedId(null)}
      dialog={dialog}
      onDialog={setDialog}
      onMutationSuccess={handleMutationSuccess}
      onResendInvite={onResendInvite}
    />
  );
}
